// src/components/scan/form/ScanFormMobile.tsx
import React, { useEffect, useState } from 'react';
import { Radar, Search, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ScanFormProps {
  domain: string;
  setDomain: (value: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  loading: boolean;
  error: string | null;
  target: number;
  onCancel: () => void;
}

const ScanFormMobile: React.FC<ScanFormProps> = ({
  domain,
  setDomain,
  onSubmit,
  loading,
  error,
  target,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const duration = 3000;

  /* Animación del contador */
  useEffect(() => {
    let start: number | null = null;
    const easeOutQuad = (t: number) => t * (2 - t);
    const animate = (ts: number) => {
      if (!start) start = ts;
      const progress = ts - start;
      const norm = Math.min(progress / duration, 1);
      setCount(Math.floor(target * easeOutQuad(norm)));
      if (progress < duration) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);

  /* Textos y clases dinámicas */
  const buttonText = loading ? t('scan.cancel') : t('scan.analyzeButton');
  const buttonCls = loading
    ? 'w-full py-4 text-lg bg-red-600 rounded-lg flex items-center justify-center gap-2 text-white'
    : 'w-full py-4 text-lg bg-accent hover:bg-yellow-600 rounded-lg flex items-center justify-center gap-2 text-white';

  return (
    <form onSubmit={onSubmit} className="w-full mx-auto px-4 mb-10">
      {/* Tarjeta con input + botón + ejemplos */}
      <div className="bg-card border border-gray-700 rounded-lg shadow-lg p-6">
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder={t('scan.inputPlaceholder')}
          className="w-full mb-4 px-4 py-3 text-base rounded-lg bg-gray-600 border border-gray-600 placeholder:text-sm focus:outline-none focus:border-blue-500 text-white"
          disabled={loading}
        />

        <button
          type={loading ? 'button' : 'submit'}
          className={buttonCls}
          onClick={() => loading && onCancel()}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> {buttonText}
            </>
          ) : (
            <>
              <Search className="w-5 h-5" /> {buttonText}
            </>
          )}
        </button>

        <div className="mt-4 text-gray-400 text-xs text-center">
          {t('scan.suggestions')}
        </div>
      </div>

      {/* Contador fuera del rectángulo, visible solo si NO está cargando */}
      {!loading && (
        <div className="flex flex-col items-center mt-6">
          <Radar className="w-8 h-8 text-primary animate-pulse" />
          <h3 className="text-3xl font-bold text-white">
            {Number(count).toLocaleString('es-ES')}
          </h3>
          <p className="text-textLight text-sm">
            {t('scan.scansPerformed')}
          </p>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div className="mt-4 text-red-400 text-center text-sm">{error}</div>
      )}
    </form>
  );
};

export default ScanFormMobile;
